import { useState } from 'react';
import type { GameType, RoomMode } from '@big2/shared';
import { Icon } from '../Icon.js';
import { useI18n } from '../../i18n.js';
import { trackEvent } from '../../analytics.js';

interface LobbyRoomHeaderProps {
  roomId: string;
  roomName?: string | null;
  gameType: GameType;
  mode: RoomMode;
  isPublic?: boolean;
  isHost: boolean;
  isPaid: boolean;
  voiceChatEnabled: boolean;
  onCopyCode: () => void;
  copiedCode: boolean;
  onCopyLink: () => void;
  copiedLink: boolean;
}

export function LobbyRoomHeader({
  roomId,
  roomName,
  gameType,
  mode,
  isPublic = false,
  isHost,
  isPaid,
  voiceChatEnabled,
  onCopyCode,
  copiedCode,
  onCopyLink,
  copiedLink,
}: LobbyRoomHeaderProps) {
  const { lang, t } = useI18n();
  const isMn = lang === 'mn';
  const isTsai = mode === 'tsai';
  const [shared, setShared] = useState(false);

  const gameLabel =
    gameType === 'daaluu'
      ? isMn ? 'Даалуу' : 'Daaluu'
      : gameType === 'twentyone'
      ? isMn ? '21 (Очко)' : '21 (Ochko)'
      : gameType === 'muushig'
      ? isMn ? 'Муушиг' : 'Muushig'
      : isMn ? 'Арван гурав' : 'Big Two';

  const gameIcon =
    gameType === 'daaluu'
      ? '🂍'
      : gameType === 'twentyone'
      ? '♦'
      : gameType === 'muushig'
      ? '♣'
      : '♠';

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const handleNativeShare = async () => {
    if (!canNativeShare) return;
    const shareUrl = `${window.location.origin}/?room=${encodeURIComponent(roomId)}`;
    const shareTitle = `${gameLabel} - ${roomName || roomId}`;
    const shareText = isMn
      ? `${gameLabel} тоглоомонд урьж байна! Өрөөний код: ${roomId}`
      : `Join my ${gameLabel} card game! Room code: ${roomId}`;

    try {
      await navigator.share({
        title: shareTitle,
        text: shareText,
        url: shareUrl,
      });
      trackEvent('room_native_shared', { room_id: roomId });
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } catch {
      // User cancelled share dialog or not supported
    }
  };

  return (
    <header className="lobby-hero-header">
      <div className="lobby-game-identity">
        <div className="lobby-badge-row">
          <div className={`lobby-game-badge${isTsai ? ' is-tsai' : ''}`}>
            <span className="lobby-game-suit" aria-hidden="true">{gameIcon}</span>
            <span>{gameLabel}</span>
            <span className="lobby-game-mode-sep" aria-hidden="true">·</span>
            <span>{isTsai ? (isMn ? '🍵 Цайтай' : '🍵 Tsai') : (isMn ? 'Чөлөөт' : 'Free')}</span>
          </div>
          <span
            className={`lobby-visibility-badge ${isPublic ? 'is-public' : 'is-private'}`}
            data-testid="lobby-visibility-badge"
          >
            {isPublic
              ? (isMn ? '🌐 Нээлттэй тоглолт' : '🌐 Public Match')
              : (isMn ? '🔒 Хувийн өрөө' : '🔒 Private Room')}
          </span>
        </div>
        <h2 className="lobby-room-title">
          <span>{roomName ? roomName : `${t.roomLabel} #${roomId}`}</span>
          {(roomName || voiceChatEnabled || (isHost && isPaid)) && (
            <span className="room-vip-tag" title="VIP">
              <Icon name="crown" size={13} /> VIP
            </span>
          )}
        </h2>
      </div>

      <div className="lobby-share-cluster">
        {/* Interactive Room Code Plaque */}
        <button
          type="button"
          className={`lobby-code-pill${copiedCode ? ' is-copied' : ''}`}
          onClick={onCopyCode}
          title={t.copyCode}
          aria-label={t.copyCode}
        >
          <span className="lobby-code-label">{isMn ? 'Өрөөний код' : 'Room code'}</span>
          <span className="lobby-code-value" data-testid="lobby-room-code">{roomId}</span>
          <span className="lobby-code-copy-hint">
            <Icon name={copiedCode ? 'check' : 'copy'} size={14} />
            <span>{copiedCode ? (isMn ? 'Хууллаа' : 'Copied') : (isMn ? 'Хуулах' : 'Copy')}</span>
          </span>
        </button>

        {/* Copy Invite Link Button */}
        <button
          type="button"
          className={`lobby-share-button${copiedLink ? ' is-copied' : ''}`}
          onClick={onCopyLink}
          title={t.copyLink}
          aria-label={t.copyLink}
        >
          <Icon name={copiedLink ? 'check' : 'link'} size={15} />
          <span>{copiedLink ? (isMn ? 'Холбоос хууллаа' : 'Link Copied') : (isMn ? 'Урих холбоос' : 'Share Link')}</span>
        </button>

        {/* Native Share on mobile */}
        {canNativeShare && (
          <button
            type="button"
            className={`lobby-share-button${shared ? ' is-copied' : ''}`}
            onClick={handleNativeShare}
            title={isMn ? 'Найзуудтайгаа хуваалцах' : 'Share room'}
            aria-label={isMn ? 'Хуваалцах' : 'Share'}
          >
            <span>{shared ? (isMn ? 'Илгээлээ' : 'Sent') : (isMn ? 'Илгээх' : 'Share')}</span>
          </button>
        )}
      </div>
    </header>
  );
}
